import { useMemo, useState } from 'react';
import { plotLabel } from '@/lib/plotLabel';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Printer, Download, Info } from 'lucide-react';
import { Drawer, LiveScreen, QueryState, RecordTable, Select, StatusPill, formatDate, formatDateTime, formatNumber, type Column } from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { useRows } from '@/hooks/useResource';
import { callRpc } from '@/api/resources';
import { toast } from '@/lib/toast';
import { downloadText, printHtml } from '@/lib/consentDocument';
import { evidencePackHtml, type PackRow } from '@/lib/evidencePack';

interface PlotOption { id: string; plot_code: string; plot_no?: string | null; plot_name?: string | null; farmer_name?: string | null }

/**
 * One monitoring instance of a plot. A plot has one per approved boundary version: amending a
 * boundary closes the instance watching the old shape and opens one on the new. A pack is about
 * ONE of them, which is why they have to be visible before you choose.
 */
interface InstanceRow {
  id: string; monitoring_code: string; plot_id: string; plot_code: string;
  geometry_version: number; area_ha: number | null; monitoring_status: string;
  activated_at: string | null; closed_at: string | null; close_reason: string | null;
  observation_count: number;
}

/** The list row: the frozen pack plus the counts the view derives for the grid. */
type PackListRow = PackRow & {
  id: string; months_covered: number; months_with_evidence: number;
  findings_count: number; open_findings_count: number;
};

const PURPOSES = [
  { id: 'fsc_audit', label: 'FSC audit' },
  { id: 'eudr_due_diligence', label: 'EUDR due diligence' },
  { id: 'internal_review', label: 'Internal review' },
  { id: 'other', label: 'Other' },
];

/** First of the month, twelve months back — the period an auditor usually asks for. */
function defaultPeriod() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: iso(from), to: iso(now) };
}

/**
 * Evidence packs: what an auditor is handed.
 *
 * A pack is frozen when it is issued — content, manifest and hash — so this screen issues and
 * reads, and never edits. A correction is a new version that names the pack it replaces, which is
 * why there is no "edit" anywhere on the page and the list keeps superseded versions in view.
 */
export default function EvidencePacksScreen() {
  const client = useQueryClient();
  const period = useMemo(defaultPeriod, []);
  const [plotId, setPlotId] = useState('');
  const [from, setFrom] = useState(period.from);
  const [to, setTo] = useState(period.to);
  const [purpose, setPurpose] = useState('fsc_audit');

  const packs = useRows<PackListRow>('evidence_packs', {
    orderBy: { column: 'issued_at', ascending: false },
  });
  const plots = useRows<PlotOption>('farm_plots', { orderBy: { column: 'plot_code', ascending: true } });
  // Every instance of the chosen plot, closed ones included: the period a superseded boundary was
  // watched can only be reported on by naming its instance.
  const instances = useRows<InstanceRow>('satellite_monitoring',
    { filters: { plot_id: plotId }, orderBy: { column: 'geometry_version', ascending: false } },
    Boolean(plotId));
  const [monitoringId, setMonitoringId] = useState('');
  const [showInstances, setShowInstances] = useState(false);
  const instanceRows = useMemo(() => instances.data ?? [], [instances.data]);

  const issue = useMutation({
    mutationFn: () => callRpc<string>('fn_issue_evidence_pack', {
      p_plot_id: plotId, p_from: from, p_to: to, p_purpose: purpose,
      // Empty means "the instance still open", which is what the server does on its own.
      p_monitoring_id: monitoringId || null,
    }),
    onSuccess: () => {
      toast.success('Evidence pack issued. It is frozen from now on.');
      client.invalidateQueries({ queryKey: ['evidence_packs'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function open(pack: PackRow, mode: 'print' | 'download') {
    const html = evidencePackHtml(pack);
    if (mode === 'download') {
      downloadText(html, `${pack.pack_code ?? 'evidence-pack'}-${pack.plot_code ?? ''}.html`);
      return;
    }
    if (!printHtml(html)) toast.error('The browser blocked the print window. Allow pop-ups for this site.');
  }

  const columns: Column<PackListRow>[] = [
    { key: 'pack_code', label: 'Pack', render: (row) => (
      <span className="cell-stack">
        <span>{row.pack_code}{row.version_no > 1 ? ` · v${row.version_no}` : ''}</span>
        <small className="muted">{formatDate(row.issued_at)}{row.issued_by_name ? ` · ${row.issued_by_name}` : ''}</small>
      </span>
    ) },
    { key: 'plot_code', label: 'Plot', render: (row) => (
      <span className="cell-stack"><span>{row.plot_code}</span><small className="muted">{row.farmer_name ?? ''}</small></span>
    ) },
    { key: 'period_from', label: 'Period', render: (row) => `${formatDate(row.period_from)} – ${formatDate(row.period_to)}` },
    { key: 'months_covered', label: 'Months seen',
      render: (row) => `${row.months_with_evidence} of ${row.months_covered}` },
    { key: 'findings_count', label: 'Findings', render: (row) => (row.findings_count === 0 ? 'None'
      : `${row.findings_count}${row.open_findings_count > 0 ? ` · ${row.open_findings_count} open` : ''}`) },
    { key: 'content_sha256', label: 'Hash', render: (row) => (
      <code style={{ fontSize: 11 }}>{row.content_sha256.slice(0, 12)}…</code>
    ) },
    { key: 'id', label: '', render: (row) => (
      <span style={{ display: 'flex', gap: 6 }}>
        <button className="btn ghost" onClick={() => open(row, 'print')} title="Print or save as PDF" type="button">
          <Printer size={15} />
        </button>
        <button className="btn ghost" onClick={() => open(row, 'download')} title="Download the document" type="button">
          <Download size={15} />
        </button>
      </span>
    ) },
  ];

  return (
    <LiveScreen
      subtitle="What an auditor is handed: one plot, one period, frozen at issue."
      title="Evidence packs"
    >
      <div className="toolbar-row">
        <label className="field"><span>Plot</span>
          <Select onChange={setPlotId} options={(plots.data ?? []).map((p) => ({ id: p.id, label: plotLabel(p) }))}
                  placeholder="Choose a plot" value={plotId} /></label>
        <label className="field"><span>From</span>
          <input onChange={(e) => setFrom(e.target.value)} type="date" value={from} /></label>
        <label className="field"><span>To</span>
          <input onChange={(e) => setTo(e.target.value)} type="date" value={to} /></label>
        <label className="field"><span>Monitoring instance</span>
          <Select
            onChange={setMonitoringId}
            options={instanceRows.map((row) => ({
              id: row.id,
              label: `${row.monitoring_code} - boundary v${row.geometry_version}${row.closed_at ? ' (closed)' : ''}`,
            }))}
            placeholder={!plotId ? 'Choose a plot first'
              : instanceRows.length ? 'The one still open' : 'This plot is not monitored'}
            value={monitoringId}
          /></label>
        {plotId && instanceRows.length > 0 && (
          <button aria-label="What are these instances?" className="btn-ghost btn-sm"
                  onClick={() => setShowInstances(true)} title="What are these instances?" type="button">
            <Info size={15} />
          </button>
        )}
        <label className="field"><span>Purpose</span>
          <Select onChange={setPurpose} options={PURPOSES} value={purpose} /></label>
        <button className="btn primary" disabled={!plotId || issue.isPending}
                onClick={() => issue.mutate()} type="button">
          Issue pack
        </button>
      </div>

      <Drawer onClose={() => setShowInstances(false)} open={showInstances}
              subtitle="A plot has one instance per approved boundary version. A pack is about one of them."
              title="Monitoring instances for this plot" wide>
        <p className="muted">
          Amending a boundary closes the instance watching the old shape and opens one on the new, so the
          history is never re-cut to a shape nobody was watching at the time. That is why a closed instance
          is still worth reporting on: it is the only record of the period its boundary was watched.
        </p>
        <RecordTable
          columns={[
            { key: 'monitoring_code', label: 'Instance', render: (row: InstanceRow) => <strong>{row.monitoring_code}</strong> },
            { key: 'geometry_version', label: 'Boundary', render: (row: InstanceRow) => `v${row.geometry_version}` },
            { key: 'area_ha', label: 'Area', align: 'right' as const, render: (row: InstanceRow) => `${formatNumber(row.area_ha, 3)} ha` },
            { key: 'observation_count', label: 'Observations', align: 'right' as const, render: (row: InstanceRow) => formatNumber(row.observation_count, 0) },
            { key: 'activated_at', label: 'Watched from', render: (row: InstanceRow) => formatDateTime(row.activated_at) },
            { key: 'closed_at', label: 'Until', render: (row: InstanceRow) => (row.closed_at ? formatDateTime(row.closed_at) : 'still watching') },
            { key: 'monitoring_status', label: 'Status', render: (row: InstanceRow) => <StatusPill value={row.monitoring_status} /> },
          ]}
          onRowClick={(row: InstanceRow) => { setMonitoringId(row.id); setShowInstances(false); }}
          rows={instanceRows}
        />
      </Drawer>

      <QueryState empty="No evidence pack has been issued yet." query={packs}>
        <DataGrid columns={columns} exportName="evidence-packs" rows={packs.data ?? []} screenKey="evidence.pack" />
      </QueryState>

      <p className="muted" style={{ fontSize: '12.5px' }}>
        A pack is frozen when it is issued: its content and its hash cannot change, and it cannot be deleted.
        If something is later corrected, issue a new version — it will name the pack it replaces and the reason.
        The pack reports what was observed and who reviewed it; it states no view on compliance.
      </p>
    </LiveScreen>
  );
}
